import { Circle } from "@phosphor-icons/react"
import { Avatar, Divider, Spinner, Tooltip } from "@vesatogo/grass-core"
import { Cell, TableView } from "@vesatogo/grass-table"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"
import { isEmpty } from "lodash-es"
import { useState } from "react"
import Codenames from "~/constants/Codenames"
import { PayableReceivableDate } from "~/constants/Payable"
import {
  Order_By,
  useCreditInvoiceSettlementQuery,
  useInvoiceSummaryAggregateQuery,
} from "~/generated/graphql"

const pageSize = 5

const INVOICE_COLUMNS = [
  {
    Header: "Created On",
    accessor: "created_at",
    Cell({ value }) {
      if (!value) return "--"
      return (
        <Cell
          title={dayjs(value).format("D MMM 'YY")}
          subtitle={dayjs(value).format("hh:mm a")}
        />
      )
    },
    formatter: () => {
      return "Total"
    },
  },
  {
    Header: "Invoice No.",
    accessor: "id",
    Cell({ value, row: { original } }) {
      if (!value) return "--"
      return (
        <div className="pl-4">
          <div className="link-text text-left w-fit cursor-pointer">
            Invoice #{value}
          </div>
        </div>
      )
    },
  },
  {
    Header: "Plant Name",
    accessor: "department.name",
    Cell({ value }) {
      return <Cell title={value} />
    },
  },

  {
    Header: "Invoice Value",
    accessor: "buyer_bifurcation.deducted_amount",
    Cell({ value, row: { original } }) {
      if (!value) return "--"
      const gst = original?.buyer_bifurcation?.buyer_tax_amount?.val || 0
      const expense =
        original?.buyer_bifurcation?.buyer_expense_amount?.val || 0
      const expenseTax =
        original?.buyer_bifurcation?.buyer_expense_tax_amount?.val || 0

      const total =
        Number(value?.val) + Number(gst) + Number(expense) + Number(expenseTax)
      return (
        <Cell
          titleClassName="font-700 !text-green"
          title={
            <Tooltip
              content={`Base: ${inr(value?.val)} | GST: ${inr(
                gst
              )} | Expense: ${inr(expense)} | Expense Tax: ${inr(expenseTax)}`}
            >
              <div>{inr(total)}</div>
            </Tooltip>
          }
          subtitle={gst ? "Incl. of taxes" : null}
        />
      )
    },
  },
  {
    Header: "Credit Note",
    accessor: "buyer_bifurcation.buyer_credit_note_amount",
    id: "creditNote",
    Cell({ value }) {
      if (!value) return "--"
      return (
        <Cell
          titleClassName="font-700 !text-red"
          title={`${value?.op + inr(value?.val)}`}
        />
      )
    },
  },
  {
    Header: "Debit Note",
    accessor: "buyer_bifurcation.buyer_debit_note_amount",
    id: "debitNote",
    Cell({ value }) {
      if (!value) return "--"
      return (
        <Cell
          titleClassName="font-700 !text-green"
          title={`${value?.op + inr(value?.val)}`}
        />
      )
    },
  },
  {
    Header: "Received Amount",
    accessor: "buyer_bifurcation.received_amount.val",
    Cell({ value }) {
      if (!value) return "--"
      return <Cell titleClassName="font-700 !text-green" title={inr(value)} />
    },
  },
  {
    Header: "Receivable Amount",
    id: "buyer_bifurcation.total",
    accessor: "buyer_bifurcation.total",
    Cell({ value }) {
      // if (!value) return "--"
      return <Cell titleClassName="font-700 !text-red" title={inr(value)} />
    },
  },
  {
    Header: "Overdue Amt",
    accessor: "summary.0.overdue_amount",
    Cell({ value }) {
      return <Cell titleClassName="font-700 !text-green" title={inr(value)} />
    },
  },
  {
    Header: "Overdue Days",
    accessor: "summary.0.overdue_days",
    Cell({ value }) {
      return <Cell titleClassName="font-700 !text-green" title={value} />
    },
  },

  {
    Header: "Placed By",
    accessor: "created_by",
    Cell({ value, row }) {
      if (!value) return "--"
      return (
        <Tooltip
          content={
            <div>
              <p>{value?.full_name || "na"}</p>
              <p className="text-gray-400">
                {dayjs(row?.original?.created_at).format("DD MMM 'YY | h:mm a")}
              </p>
            </div>
          }
        >
          <Avatar
            className="relative text-center"
            name={value?.full_name}
            src={value?.photo?.url}
          />
        </Tooltip>
      )
    },
  },
  {
    Header: "Transaction Status",
    accessor: "transactions",
    Cell({ row: { original }, value }) {
      const paid = original?.buyer_bifurcation?.received_amount?.val
      const remaining = Number(
        Number(original?.buyer_bifurcation?.total).toFixed(2)
      )
      const isSettled = remaining === 0 || remaining < 0
      const isInvoiceCancelled =
        original?.current_status_id === Codenames.InvoiceCancelled
      return (
        <Cell
          titleClassName={classNames(
            "flex gap-1 items-center font-600 ",
            isInvoiceCancelled
              ? "!text-red"
              : isSettled
              ? "!text-green"
              : paid
              ? "!text-blue"
              : "!text-yellow"
          )}
          title={
            <>
              <Circle weight="fill" />
              {isInvoiceCancelled
                ? "Cancelled"
                : isSettled
                ? "Settled"
                : paid
                ? "Partial"
                : "Pending"}
            </>
          }
        />
      )
    },
  },
]

export const Invoice = ({ form }) => {
  const [page, setPage] = useState(1)
  const { values } = form
  const invoice = values?.invoice
  const [{ data, fetching: listFetching }] = useCreditInvoiceSettlementQuery({
    variables: {
      where: {
        ...(invoice?.placed_by
          ? {
              created_by_id: {
                _eq: invoice?.placed_by?.id?.toString(),
              },
            }
          : undefined),
        buyer_settlement_status: {
          _neq: "settled",
        },
        current_status_id: {
          _neq: "Invoice Cancelled",
        },
        buyer: {
          id: {
            _eq: values?.user?.shared_id,
          },
        },
        created_at: {
          _lte: invoice?.to ? dayjs(invoice?.to).endOf("d") : undefined,
          _gte: invoice?.from
            ? dayjs(invoice?.from).startOf("d")
            : dayjs(PayableReceivableDate).startOf("d"),
        },
      },
      limit: pageSize,
      offset: (page - 1) * pageSize,
      orderBy: {
        created_at: Order_By.DescNullsLast,
      },
    },
    pause: !values?.user?.shared_id,
  })
  const totalRows = data?.invoice_aggregate?.aggregate?.count || 0

  const [{ data: summaryAggregate, fetching: aggregateFetching }] =
    useInvoiceSummaryAggregateQuery({
      variables: {
        from: invoice?.from
          ? dayjs(invoice?.from).format("YYYY-MM-DD")
          : dayjs(PayableReceivableDate).format("YYYY-MM-DD"),
        to: invoice?.to ? dayjs(invoice?.to).format("YYYY-MM-DD") : undefined,
        userId: values?.user?.shared_id,
        placedBy: invoice?.placed_by?.id?.toString(),
      },
      pause: isEmpty(data?.invoice),
    })
  const totalInvoiceAmt =
    summaryAggregate?.invoice_summary_by_user?.total_invoice_amount || 0

  const totalReceivedAmt =
    summaryAggregate?.invoice_summary_by_user?.received_amount || 0

  const totalBalance =
    summaryAggregate?.invoice_summary_by_user
      ?.department_settlement_remaining_amount || 0

  const totalReceivableAmt = totalInvoiceAmt - totalReceivedAmt

  const totalNetReceivable =
    Number(
      Number(
        (totalReceivableAmt || 0) -
          (summaryAggregate?.invoice_summary_by_user
            ?.department_settlement_remaining_amount || 0)
      )?.toFixed(2)
    ) || 0

  const totalOverdueAmt =
    summaryAggregate?.invoice_summary_by_user?.overdue_amount || 0

  const avgOverdueDays =
    summaryAggregate?.invoice_summary_by_user?.overdue_days || 0

  const avgPaymentClearanceTime =
    summaryAggregate?.invoice_summary_by_user?.payment_cleared_in_days || 0

  const analyticsColumn = [
    {
      id: "invoice_amt",
      title: "Invoice Amount",
      value: inr(totalInvoiceAmt),
      valueClassName: "text-blue-300 text-xl",
      bg: "",
    },
    {
      id: "received",
      title: "Received Amount",
      value: inr(totalReceivedAmt),
      valueClassName: "text-green text-xl",
      unit: "ha",
    },
    {
      id: "balance",
      title: "Balance Amount",
      value: inr(totalBalance),
      valueClassName: "text-brown text-xl",
      unit: "ha",
    },
    {
      id: "receivable",
      title: "Receivable Amount",
      value: inr(totalReceivableAmt),
      valueClassName: "text-red-500 text-xl",
    },
    {
      id: "net_receivable",
      title: "Net Receivable Amount",
      value: inr(totalNetReceivable),
      valueClassName: "text-red-500 text-xl",
    },
    {
      id: "overdue",
      title: "Overdue Amount",
      value: inr(totalOverdueAmt),
      valueClassName: "text-red-500 text-xl",
    },
    {
      id: "avg_overdue_days",
      title: "Average Overdue Days",
      value: avgOverdueDays + " days",
      valueClassName: "text-red-500 text-xl",
    },
    {
      id: "clearance_time",
      title: "Average Payment Clearance Time",
      value: avgPaymentClearanceTime + " days",
      valueClassName: "text-blue-300 text-xl",
    },
  ]

  return (
    <>
      <section className="m-4 bg-white mb-0 rounded-t-xl">
        <div className="grid grid-cols-8 gap-2 p-4 bg-gray-100 rounded-2xl">
          {analyticsColumn?.map((col, idx) => {
            return (
              <div key={col?.id + idx} className="flex">
                <div key={col?.id} className="!w-full">
                  <div className="flex flex-col text-center">
                    <div className={classNames("text-sm")}>{col?.title}</div>
                    {
                      <div
                        className={classNames("font-700", col?.valueClassName)}
                      >
                        {listFetching || aggregateFetching ? (
                          <Spinner />
                        ) : (
                          col?.value || "NA"
                        )}{" "}
                      </div>
                    }
                  </div>
                </div>

                <div />
                {idx !== analyticsColumn?.length - 1 && <Divider />}
              </div>
            )
          })}
        </div>
      </section>
      <section className={classNames("mt-2 bg-gray-100 rounded-lg text-sm")}>
        <TableView
          data={data?.invoice || []}
          columns={INVOICE_COLUMNS}
          tableClassName="!m-0 !mb-4"
          isLoading={listFetching}
          paginationProps={{
            total: Math.ceil(totalRows / pageSize),
            page: page,
            onChange(page) {
              setPage(page)
            },
          }}
        ></TableView>
      </section>
    </>
  )
}
